import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import CompanyServices from './company.services';
import csvDownload from 'json-to-csv-export';

// Helper function to format date strings
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// Helper function to add auto-filter to a worksheet
const addAutoFilter = (worksheet) => {
    if (!worksheet || !worksheet['!ref']) return worksheet;
    
    // Set auto-filter on the worksheet
    worksheet['!autofilter'] = { ref: worksheet['!ref'] };
    
    return worksheet;
};

// Export users as CSV
export const exportUsers = async (users, companyId, workspacesMapping = {}) => {
    try {
        const filteredUsers = users; // Users are already filtered in the component
        
        const csvData = filteredUsers.length > 0 
            ? filteredUsers.map(user => {
                // Get default workspace ID and find corresponding name
                const defaultWorkspaceId = user.user.activeWorkspaceId;
                const workspaceName = workspacesMapping[defaultWorkspaceId] || '';
                
                return {
                    _id: user.user._id,
                    firstName: user.user.firstName,
                    lastName: user.user.lastName,
                    email: user.user.email,
                    status: user.user.pending === true ? 'Pending' : 'Active',
                    timezone: user.user.timezone || '',
                    language: user.user.language || '',
                    created: user.user.created,
                    modified: user.user.modified,
                    // isExternalIdpUser: user.user.isExternalIdpUser === null ? '' : String(user.user.isExternalIdpUser),
                    // isInternal: String(user.user.isInternal),
                    lastActiveDate: user.user.lastActiveDate || '',
                    defaultWorkspace: workspaceName,
                    allWorkspaces: user.workspaces.join(', ') || '',
                    isOnAdminWorkspace: user.user.isOnAdminWorkspace === null ? '' : String(user.user.isOnAdminWorkspace),
                    "default.explore": user.user.appPermissions?.explore || 'NONE',
                    "default.analyze": user.user.appPermissions?.analyze || 'NONE',
                    "default.mediaRelations": user.user.appPermissions?.mediaRelations || 'NONE',
                    "default.engage": user.user.appPermissions?.engage || 'NONE',
                    "default.share": user.user.appPermissions?.share || 'NONE',
                    "default.report": user.user.appPermissions?.report || 'NONE',
                    "default.account": user.user.appPermissions?.account || 'NONE',
                    "default.manageUsers": user.user.appPermissions?.manageUsers || 'NONE',
                    "default.explorePlus": user.user.appPermissions?.explorePlus || 'NONE',
                    "default.meltwaterApi": user.user.appPermissions?.meltwaterApi || 'NONE',
                    "default.dashboards": user.user.appPermissions?.dashboards || 'NONE'
                };
            })
            : [{ firstName: '', lastName: '', email: '', status: '', timezone: '', language: '', created: '', modified: '', lastActiveDate: '', defaultWorkspace: '', allWorkspaces: '', isOnAdminWorkspace: '', "default.explore": '', "default.analyze": '', "default.mediaRelations": '', "default.engage": '', "default.share": '', "default.report": '', "default.account": '', "default.manageUsers": '', "default.explorePlus": '', "default.meltwaterApi": '', "default.dashboards": '' }];

        const csvConfig = {
            data: csvData,
            delimiter: ',',
            filename: `users_export_${companyId}_${new Date().toISOString().split('T')[0]}`
        };

        csvDownload(csvConfig);
        
        return {
            success: true,
            count: filteredUsers.length
        };
    } catch (error) {
        console.error('User export error:', error);
        throw error;
    }
};

// Export all workspaces to a single consolidated Excel file
// Note: Users are assumed to be already filtered in the component if needed
export const exportWorkspacesConsolidated = async (workspaces, companyId) => {
    // Get company name from metadata if available
    let companyName = 'company';
    try {
        const companyInfo = await CompanyServices.getCompanyInfo(companyId);
        companyName = companyInfo.data.name || 'company';
        // Sanitize company name for filename
        companyName = companyName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    } catch (err) {
        console.error('Error fetching company name:', err);
    }

    const timestamp = new Date().toISOString().split('T')[0];
    const workbook = XLSX.utils.book_new();
    
    // Create consolidated data sheets
    const allData = {
        summary: [],
        monitors: [],
        users: [],
        searches: [],
        categories: [],
        filters: [],
        tags: [],
        dashboards: [],
        authorLists: [],
        comparisons: [],
        monitoredConnections: []
    };

    // Process each workspace and consolidate data
    for (const workspace of workspaces) {
        // Add summary data
        allData.summary.push({
            'Workspace ID': workspace._id,
            'Workspace Name': workspace.name,
            'Description': workspace.description || '',
            'Created Date': formatDate(workspace.created),
            'Users': workspace.totalUserCount || 0,
            'Searches': workspace.searches?.length || 0,
            'Custom Categories': workspace.customCategories?.length || 0,
            'Filter Sets': workspace.filters?.length || 0,
            'Author Lists': workspace.authorLists?.length || 0,
            'Comparisons': workspace.comparisons?.length || 0,
            'Monitors': workspace.monitors?.length || 0,
            'Tags': workspace.documentTags?.length || 0,
            'Monitored Connections': workspace.monitoredConnections?.totalCount || 0,
            'Dashboards': (workspace.dashboards?.custom?.length || 0) + (workspace.dashboards?.classic?.length || 0),
        });

        // Process monitors
        (workspace.monitors || []).forEach(monitor => {
            allData.monitors.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Monitor ID': monitor.id,
                'Monitor Name': monitor.name
            });
        });

        // Process monitored connections
        const facebookConnections = (workspace.monitoredConnections?.facebook || []);
        const instagramConnections = (workspace.monitoredConnections?.instagram || []);
        const linkedinConnections = (workspace.monitoredConnections?.linkedin || []);
        
        // Add Facebook connections
        facebookConnections.forEach(connection => {
            allData.monitoredConnections.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Connection ID': connection.id,
                'Connection Name': connection.name,
                'Type': connection.type,
                'Status': connection.status,
                'Link': connection.link || ''
            });
        });
        
        // Add Instagram connections
        instagramConnections.forEach(connection => {
            allData.monitoredConnections.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Connection ID': connection.id,
                'Connection Name': connection.name,
                'Type': connection.type,
                'Status': connection.status
            });
        });
        
        // Add LinkedIn connections
        linkedinConnections.forEach(connection => {
            allData.monitoredConnections.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Connection ID': connection.id,
                'Connection Name': connection.name,
                'Type': connection.type,
                'Status': connection.status
            });
        });

        // Process users (already filtered in the component)
        (workspace.users || [])
            .forEach(user => {
                allData.users.push({
                    'Workspace ID': workspace._id,
                    'Workspace Name': workspace.name,
                    'Email': user.email,
                    'First Name': user.firstName,
                    'Last Name': user.lastName,
                    'Last Active': formatDate(user.companyMemberships?.[0]?.lastActiveDate),
                    'Internal User': user.isInternal ? 'Yes' : 'No'
                });
            });

        // Process searches
        (workspace.searches || []).forEach(search => {
            allData.searches.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Search Name': search.name,
                'Creator': search.creator?.fullName || 'Unknown',
                'Information Types': search.informationTypes?.join(', ') || '',
                'Read Only': search.permissions?.readOnly ? 'Yes' : 'No'
            });
        });

        // Process categories
        (workspace.customCategories || []).forEach(category => {
            allData.categories.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Category Name': category.name,
                'Company Wide': category.companyWide ? 'Yes' : 'No',
                'Read Only': category.permissions?.readOnly ? 'Yes' : 'No'
            });
        });

        // Process filters
        (workspace.filters || []).forEach(filter => {
            allData.filters.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Filter Name': filter.name,
                'Company Wide': filter.companyWide ? 'Yes' : 'No',
                'Read Only': filter.permissions?.readOnly ? 'Yes' : 'No'
            });
        });

        // Process tags
        (workspace.documentTags || []).forEach(tag => {
            allData.tags.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Tag Name': tag.name,
                'Creator': tag.creator?.fullName || 'Unknown'
            });
        });

        // Process dashboards
        [
            ...(workspace.dashboards?.custom || []).map(dashboard => ({
                ...dashboard,
                type: 'Custom'
            })),
            ...(workspace.dashboards?.classic || []).map(dashboard => ({
                ...dashboard,
                type: 'Classic'
            }))
        ].forEach(dashboard => {
            allData.dashboards.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Dashboard Name': dashboard.name,
                'Type': dashboard.type
            });
        });

        // Process author lists
        (workspace.authorLists || []).forEach(list => {
            allData.authorLists.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Author List Name': list.name,
                'ID': list.id,
                'Company Wide': list.companyWide ? 'Yes' : 'No',
                'Read Only': list.permissions?.readOnly ? 'Yes' : 'No'
            });
        });

        // Process comparisons
        (workspace.comparisons || []).forEach(comparison => {
            allData.comparisons.push({
                'Workspace ID': workspace._id,
                'Workspace Name': workspace.name,
                'Comparison Name': comparison.name,
                'ID': comparison.id
            });
        });
    }

    // Create sheets with the consolidated data and add auto-filter
    if (allData.summary.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.summary);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Summary');
    }

    if (allData.users.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.users);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Users');
    }

    if (allData.searches.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.searches);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Searches');
    }

    if (allData.categories.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.categories);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Custom Categories');
    }

    if (allData.filters.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.filters);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Filter Sets');
    }

    if (allData.authorLists.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.authorLists);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Author Lists');
    }

    if (allData.comparisons.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.comparisons);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Comparisons');
    }

    if (allData.monitors.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.monitors);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Monitors');
    }

    if (allData.tags.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.tags);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Tags');
    }

    if (allData.monitoredConnections.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.monitoredConnections);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Monitored Connections');
    }

    if (allData.dashboards.length > 0) {
        const sheet = XLSX.utils.json_to_sheet(allData.dashboards);
        addAutoFilter(sheet);
        XLSX.utils.book_append_sheet(workbook, sheet, 'Dashboards');
    }

    // Generate the Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Save the file
    saveAs(blob, `workspace_export_${companyName}_${companyId}_${timestamp}.xlsx`);
};

/**
 * Track export operations with flexible format support
 * @param {string} companyId - The company ID
 * @param {Object|Array} data - Export data object or array of items being exported
 * @param {string} [format] - Format identifier ('workspace', 'user', or custom format)
 * @param {Object} [options] - Additional options for the specific format
 */
export const trackExport = async (companyId, data, format, options = {}) => {
    try {
        let exportData;

        // Handle different export format types
        switch (format) {
            // Legacy workspace export format
            case 'workspace':
                exportData = {
                    type: options.type || 'AllWorkspaces',
                    eventType: options.eventType || 'exportWorkspaceData',
                    totalWorkspaces: data.length,
                    exportedWorkspaces: data.length,
                    includeInternalUsers: options.includeInternal || false
                };
                break;

            // User export format  
            case 'user':
                exportData = {
                    type: options.type || 'All',
                    eventType: 'exportUser',
                    totalUsers: options.totalUsers || data.length,
                    exportedUsers: data.length,
                    includeInternalUsers: options.includeInternal || false
                };
                break;
                
            // Future formats can be added as new cases
            // case 'newFormat':
            //    exportData = {...};
            //    break;

            // Legacy compatibility - directly passed export data object
            default:
                // If data is an array and we have a type+eventType in options, assume legacy workspace format
                if (Array.isArray(data) && options.type && options.eventType) {
                    exportData = {
                        type: options.type,
                        eventType: options.eventType,
                        totalWorkspaces: data.length,
                        exportedWorkspaces: data.length,
                        includeInternalUsers: options.includeInternal || false
                    };
                } 
                // If data is an object, use it directly
                else if (typeof data === 'object' && !Array.isArray(data)) {
                    exportData = data;
                } 
                // Fallback case
                else {
                    console.warn('Unrecognized export format. Using default tracking data.');
                    exportData = {
                        type: 'Unknown',
                        eventType: 'exportData',
                        items: Array.isArray(data) ? data.length : 1
                    };
                }
        }

        await CompanyServices.trackExportOperation(companyId, exportData);
    } catch (trackingError) {
        console.error('Export tracking error:', trackingError);
    }
};